import { BubbleMenu, Editor } from '@tiptap/react';
import { Button, Textarea, Typography } from 'components';
import { ENTER } from 'constants/keyboard';
import {
  GeneratedContentCustomPromptPayload,
  GeneratedContentImproveWritingPayload,
  GeneratedContentMakeLongerPayload,
  GeneratedContentMakeShorterPayload
} from 'gql/graphql';
import {
  CloseIcon,
  GlobeAiIcon,
  ImproveIcon,
  MakeLargerIcon,
  MakeShortenIcon,
  RegenerationIcon,
  SendIcon
} from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from 'utils/cn';
import { useContentGenerationContext } from './_context/ContentGenerationContext';

interface IImprovingMenu {
  editor: Editor | null;
}

enum Improvements {
  AskTolq = 'ask',
  Improve = 'improve',
  Shorten = 'shorten',
  Expand = 'expand'
}

const ImprovingMenu: FC<IImprovingMenu> = ({ editor }) => {
  const t = useTranslations();
  const [action, setAction] = useState<Improvements | ''>('');
  const [prompt, setPrompt] = useState('');
  const [selection, setSelection] = useState({ text: '', from: 0, to: 0 });
  const [phase, setPhase] = useState(0);
  const [spin, setSpin] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [height, setHeight] = useState<string | number>(32);

  const {
    generatedContent,
    targetLanguage,
    isImproving,
    isChecking,
    isNotInitialized,
    contentGenerationAsk,
    contentGenerationExpand,
    contentGenerationImprove,
    contentGenerationShorten,
    handleChangeWordsCount
  } = useContentGenerationContext();

  const resizeTextarea = useCallback(
    (value: string) => {
      if (inputRef?.current?.style?.height && (value?.length > 65 || value.includes('\n'))) {
        inputRef.current.style.height = 'auto'; // Reset height
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
        return setHeight(inputRef.current.scrollHeight);
      }
      return setHeight(32);
    },
    [inputRef]
  );

  const handlePrompt = useCallback(
    ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
      setPrompt(target.value);
      resizeTextarea(target.value);
    },
    [setPrompt, resizeTextarea]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) =>
    prompt?.length > 0 && e.key === ENTER && !e.shiftKey && handleAsk();

  const handleAdjustments = useCallback(
    ({ target }: ChangeEvent<HTMLTextAreaElement>) =>
      setSelection((oldState) => ({ ...oldState, text: target.value })),
    [setPrompt, setSelection]
  );

  const handleReplace = useCallback(() => {
    editor?.commands?.insertContentAt({ from: selection.from, to: selection.to }, selection.text);
    handleChangeWordsCount();
  }, [editor, selection, handleChangeWordsCount]);

  const handleImprovementApi = useCallback(
    async (
      newAction: Improvements,
      callback: (
        generatedContentGenerationId: string
      ) => Promise<
        | GeneratedContentImproveWritingPayload
        | GeneratedContentMakeLongerPayload
        | GeneratedContentMakeLongerPayload
        | GeneratedContentMakeShorterPayload
        | GeneratedContentCustomPromptPayload
      >
    ) => {
      const generatedContentGenerationId = generatedContent.generations?.find(
        (generation) => generation.targetLanguage === targetLanguage
      )?.id;
      if (generatedContentGenerationId) {
        setSelection((oldState) => ({ ...oldState, text: selection.text }));
        setAction(newAction);
        setPhase(1);
        const response = await callback(generatedContentGenerationId);
        setSelection((oldState) => ({ ...oldState, text: response.text ?? '' }));
      }
    },
    [selection, generatedContent, targetLanguage, setSelection]
  );

  const handleAsk = useCallback(
    () =>
      handleImprovementApi(Improvements.AskTolq, (generatedContentGenerationId: string) =>
        contentGenerationAsk({ content: selection.text, generatedContentGenerationId, prompt })
      ),
    [selection, prompt, contentGenerationAsk, handleImprovementApi]
  );

  const handleImprove = useCallback(
    () =>
      handleImprovementApi(Improvements.Improve, (generatedContentGenerationId: string) =>
        contentGenerationImprove({ content: selection.text, generatedContentGenerationId })
      ),
    [selection, contentGenerationImprove, handleImprovementApi]
  );

  const handleShorten = useCallback(
    () =>
      handleImprovementApi(Improvements.Shorten, (generatedContentGenerationId: string) =>
        contentGenerationShorten({ content: selection.text, generatedContentGenerationId })
      ),
    [selection, contentGenerationShorten, handleImprovementApi]
  );

  const handleExpand = useCallback(
    () =>
      handleImprovementApi(Improvements.Expand, (generatedContentGenerationId: string) =>
        contentGenerationExpand({ content: selection.text, generatedContentGenerationId })
      ),
    [selection, contentGenerationExpand, handleImprovementApi]
  );

  const actions = {
    [Improvements.AskTolq]: handleAsk,
    [Improvements.Improve]: handleImprove,
    [Improvements.Expand]: handleExpand,
    [Improvements.Shorten]: handleShorten
  } as Record<Improvements, () => Promise<void>>;

  const handleSelection = useCallback(() => {
    if (editor) {
      const { state } = editor;
      const { from, to } = state.selection;

      handleChangeWordsCount();
      editor?.chain().focus().setTextSelection({ from, to }).toggleHighlight().run();

      return setSelection({ text: state.doc.textBetween(from, to, ' '), from, to });
    }
  }, [editor, selection, setSelection, handleChangeWordsCount()]);

  const handleOnBlur = useCallback(() => {
    if (editor) {
      setPhase(0);
      setPrompt('');
      resizeTextarea('');
      setAction('');
      setSelection({ text: '', from: 0, to: 0 });
      return editor?.commands?.setContent(
        editor?.getHTML().replaceAll('<mark>', '').replaceAll('</mark>', '')
      );
    }
  }, [editor, setPhase, setPrompt, resizeTextarea, setAction, setSelection]);

  const handleReload = useCallback(async () => {
    if (action) {
      setSpin(true);
      await actions[action]();
      setSpin(false);
    }
  }, [action, handleAsk, handleImprove, handleExpand, handleShorten]);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current?.focus();
    }
  }, [phase]);

  if (!editor || isChecking || isNotInitialized) return null;

  return (
    <BubbleMenu
      className={cn('relative', !selection.text?.length ? 'hidden' : '')}
      tippyOptions={{
        duration: 100,
        placement: 'bottom-start',
        onHidden: handleOnBlur,
        onMount: handleSelection
      }}
      editor={editor}
    >
      {phase === 0 && (
        <div>
          <div className="relative flex items-end justify-between transition-all">
            <div className="absolute left-2.5 top-[12px] z-[999999]">
              <GlobeAiIcon className="shadow-[0px_5px_9px_-6px_rgba(0,0,0,0.1)]" />
            </div>
            <Textarea
              placeholder={t('placeholders.tell_tolq')}
              value={prompt || ''}
              className={cn(
                'z-[10] h-8 !w-[500px] !pl-8 !pr-9 !pt-2 shadow-custom-lighten',
                'w-full text-xl outline-none',
                'mr-4 font-content font-light',
                'resize-none overflow-hidden rounded outline-none',
                'placeholder:text-tertiary-400 disabled:bg-transparent disabled:text-tertiary-300'
              )}
              autoFocus
              onChange={handlePrompt}
              onKeyDown={handleKeyDown}
              style={{ height }}
              ref={inputRef}
            />
            <div className="relative flex flex-1 flex-col items-end">
              <Button
                size="small"
                variant="icon"
                color="none"
                className={cn(
                  'absolute -top-3 right-[46px] !m-0 !h-6 !rounded-3xl !bg-secondary-green !p-1 !px-1.5',
                  !prompt?.length
                    ? '!pointer-events-none !cursor-not-allowed !bg-primary-blue-800'
                    : ''
                )}
                onClick={handleAsk}
                startAdornment={<SendIcon />}
              />
            </div>
          </div>
          <div className="absolute z-[100] mt-2 !w-fit space-y-2 rounded-md bg-white p-1 shadow-custom-light">
            <Button
              variant="icon"
              colorIcon="black"
              className="flex !h-7 !w-full !items-start !rounded !px-2 !py-0 hover:!bg-primary-blue-900"
              startAdornment={<ImproveIcon />}
              onClick={handleImprove}
            >
              <Typography
                variant="regular4"
                className="flex cursor-pointer select-none items-center gap-2 text-tertiary-100"
              >
                {t('content_generation.ai_bar.improve_writing')}
              </Typography>
            </Button>
            <Button
              variant="icon"
              colorIcon="black"
              className="flex !h-7 !w-full !items-start !rounded !px-2 !py-0 hover:!bg-primary-blue-900"
              startAdornment={<MakeShortenIcon />}
              onClick={handleShorten}
            >
              <Typography
                variant="regular4"
                className="flex cursor-pointer select-none items-center gap-2 text-tertiary-100"
              >
                {t('content_generation.ai_bar.shorten')}
              </Typography>
            </Button>
            <Button
              variant="icon"
              colorIcon="black"
              className="flex !h-7 !w-full !items-start !rounded !px-2 !py-0 hover:!bg-primary-blue-900"
              startAdornment={<MakeLargerIcon />}
              onClick={handleExpand}
            >
              <Typography
                variant="regular4"
                className="flex select-none items-center gap-2 text-tertiary-100"
              >
                {t('content_generation.ai_bar.expand')}
              </Typography>
            </Button>
          </div>
        </div>
      )}
      {phase === 1 && (
        <div
          className={cn(
            'absolute z-[100] !w-fit space-y-2 rounded-md bg-white p-4 shadow-custom-light',
            'flex flex-col'
          )}
        >
          <Textarea
            value={selection.text || ''}
            placeholder={t('placeholders.tell_tolq')}
            onChange={handleAdjustments}
            className="z-[10] !w-[35vw] shadow-custom-lighten"
            autoFocus
            rows={5}
            disabled={isImproving}
          />
          <div className="flex justify-between pt-2">
            <div className="flex gap-4">
              <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.discard')}>
                <Button
                  size="small"
                  variant="icon"
                  color="neutral"
                  startAdornment={<CloseIcon />}
                  onClick={handleOnBlur}
                />
              </div>
              <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('tooltips.reload')}>
                <Button
                  size="small"
                  variant="icon"
                  color="neutral"
                  onClick={handleReload}
                  className={cn(
                    '!bg-transparent',
                    spin ? '!pointer-events-none !cursor-not-allowed' : ''
                  )}
                  disabled={action === ''}
                  startAdornment={
                    <span className="scale-[0.8]">
                      <RegenerationIcon className={spin ? 'animate-spin' : ''} />
                    </span>
                  }
                />
              </div>
            </div>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              label={t('buttons.replace')}
              disabled={isImproving}
              loading={isImproving && !spin}
              classNameLoading="top-[0px]"
              onClick={handleReplace}
            />
          </div>
        </div>
      )}
    </BubbleMenu>
  );
};

export default ImprovingMenu;
